import { EyeIcon, TrashIcon } from '@heroicons/react/24/outline'
import { itemCountPerPage } from 'config'
import { Pagination } from 'stories/components'
import { formatTime } from 'utils'
import { renderHeader } from 'utils/table'

import { type ITicket, TicketStatusColorMap, TicketStatusText } from './types'

interface InternalTicketsTableProps {
  values: ITicket[]
  pageNum: number
  total: number
  filters: Record<string, any>
  onSort: (key: string, sortOrder: number) => void
  goToMessages: (value: ITicket) => void
  onDelete: (ticket: ITicket) => void
  onPageNavigate: (num: number) => void
}

export const InternalTicketsTable = ({
  values,
  pageNum,
  filters,
  total,
  onSort,
  goToMessages,
  onDelete,
  onPageNavigate,
}: InternalTicketsTableProps) => {
  return (
    <div className="table-container relative overflow-x-auto shadow-md sm:rounded-lg mb-6">
      <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
        <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
          <tr className="font-variation-settings-600">
            <th scope="col" className="px-2 py-3 w-[40px]">
              No
            </th>

            <th scope="col" className="py-2 px-2">
              Loan Number
            </th>

            <th scope="col" className="py-2 px-2">
              Creator
            </th>

            {renderHeader({
              title: 'Subject',
              key: 'title',
              index: 2,
              onSort,
              sortable: true,
              sortOrder: filters.orderBy == 'title' ? parseInt(filters.orderDir) : 0,
            })}

            {renderHeader({
              title: 'Status',
              key: 'status',
              index: 3,
              onSort,
              sortable: true,
              sortOrder: filters.orderBy == 'status' ? parseInt(filters.orderDir) : 0,
            })}
            {renderHeader({
              title: 'Created At',
              key: 'createdAt',
              index: 4,
              onSort,
              sortable: true,
              sortOrder: filters.orderBy == 'createdAt' ? parseInt(filters.orderDir) : 0,
            })}
            <th scope="col" className="px-2 py-3 w-[100px]">
              Action
            </th>
          </tr>
        </thead>

        <tbody className="text-[14px] text-gray-900">
          {values.map((value, index) => {
            return (
              <tr key={`${value.id}-${index}`} className={`border-b ${index % 2 && 'bg-slate-50'}`}>
                <td className="pl-3 py-3">{index + 1 + pageNum * itemCountPerPage}</td>
                <td className="px-2 py-2">{value.loanNumber}</td>
                <td className="px-2 py-2">{value?.userInfo?.name ? `${value?.userInfo?.name}` : ''}</td>
                <td
                  className="font-variation-settings-600 text-shade-blue hover:underline cursor-pointer pl-3 py-3 text-base"
                  onClick={() => goToMessages(value)}
                >
                  {value.title}
                </td>

                <td className="px-2 py-2">
                  <span className={`${TicketStatusColorMap[value.status]}`}>{TicketStatusText[value.status]}</span>
                </td>

                <td className="px-2 py-2">{formatTime(value.createdAt)}</td>
                <td className="px-2 py-2">
                  <span className="flex gap-2">
                    <button
                      className="text-shade-blue hover-shadow1 cursor-pointer p-1"
                      onClick={() => goToMessages(value)}
                    >
                      <EyeIcon className="w-4 h-4"></EyeIcon>
                    </button>
                    <button
                      className="text-red-600 hover-shadow1 cursor-pointer p-1"
                      onClick={() => onDelete(value)}
                      title="Delete ticket"
                    >
                      <TrashIcon className="w-4 h-4"></TrashIcon>
                    </button>
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="flex justify-end items-center mt-3">
        <Pagination
          totalCount={total}
          itemCountPerPage={itemCountPerPage}
          onNavigate={onPageNavigate}
          pageNum={pageNum}
        />
      </div>
    </div>
  )
}
